import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { trackEvent } from '../../../hooks/useGoogleAnalytics';

const TripForm = ({ onSubmit, currentLanguage, editingTrip, onCancelEdit }) => {
  const [formData, setFormData] = useState({
    fromCountry: '',
    toCountry: '',
    departureDate: '',
    returnDate: '',
    capacityKg: '',
    allowedCategories: [],
    // Flight information fields
    airline: '',
    flightNumber: '',
    departureAirport: '',
    departureTerminal: '',
    departureTime: '',
    arrivalAirport: '',
    arrivalTerminal: '',
    arrivalTime: '',
    flightTicket: null
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [ticketPreview, setTicketPreview] = useState(null);

  useEffect(() => {
    if (editingTrip) {
      setFormData({
        fromCountry: editingTrip?.fromCountry || '',
        toCountry: editingTrip?.toCountry || '',
        departureDate: editingTrip?.departureDate || '',
        returnDate: editingTrip?.returnDate || '',
        capacityKg: editingTrip?.capacityKg?.toString() || '',
        allowedCategories: editingTrip?.allowedCategories || [],
        airline: editingTrip?.flightInfo?.airline || '',
        flightNumber: editingTrip?.flightInfo?.flightNumber || '',
        departureAirport: editingTrip?.flightInfo?.departureAirport || '',
        departureTerminal: editingTrip?.flightInfo?.departureTerminal || '',
        departureTime: editingTrip?.flightInfo?.departureTime || '',
        arrivalAirport: editingTrip?.flightInfo?.arrivalAirport || '',
        arrivalTerminal: editingTrip?.flightInfo?.arrivalTerminal || '',
        arrivalTime: editingTrip?.flightInfo?.arrivalTime || '',
        flightTicket: null
      });
      if (editingTrip?.flightInfo?.ticketFilePath) {
        setTicketPreview(editingTrip?.flightInfo?.ticketFilePath);
      }
    }
  }, [editingTrip]);

  const translations = {
    en: {
      createTrip: 'Create Trip',
      editTrip: 'Edit Trip',
      fromCountry: 'From Country',
      selectFrom: 'Select departure country',
      toCountry: 'To Country',
      selectTo: 'Select destination country',
      departureDate: 'Departure Date',
      returnDate: 'Return Date',
      capacity: 'Capacity (kg)',
      capacityPlaceholder: 'Enter max capacity',
      allowedCategories: 'Allowed Item Categories',
      selectCategories: 'Select categories you can carry',
      flightInfo: 'Flight Information',
      airline: 'Airline',
      selectAirline: 'Select airline',
      flightNumber: 'Flight Number',
      flightNumberPlaceholder: 'e.g., KE001',
      departureAirport: 'Departure Airport',
      selectDepartureAirport: 'Select departure airport',
      departureTerminal: 'Departure Terminal (Optional)',
      terminalPlaceholder: 'e.g., Terminal 1',
      departureTime: 'Departure Time',
      arrivalAirport: 'Arrival Airport',
      selectArrivalAirport: 'Select arrival airport',
      arrivalTerminal: 'Arrival Terminal (Optional)',
      arrivalTime: 'Arrival Time',
      flightTicket: 'Flight Ticket Upload (Optional)',
      uploadTicket: 'Upload Ticket',
      ticketUploaded: 'Ticket Uploaded',
      removeTicket: 'Remove',
      submit: 'Create Trip',
      update: 'Update Trip',
      cancel: 'Cancel',
      requiredField: 'This field is required',
      invalidDate: 'Return date must be after departure date',
      invalidTime: 'Arrival time must be after departure time'
    },
    ko: {
      createTrip: '여행 생성',
      editTrip: '여행 수정',
      fromCountry: '출발 국가',
      selectFrom: '출발 국가 선택',
      toCountry: '도착 국가',
      selectTo: '도착 국가 선택',
      departureDate: '출발일',
      returnDate: '귀국일',
      capacity: '용량 (kg)',
      capacityPlaceholder: '최대 용량 입력',
      allowedCategories: '허용 품목 카테고리',
      selectCategories: '운반 가능한 카테고리 선택',
      flightInfo: '항공편 정보',
      airline: '항공사',
      selectAirline: '항공사 선택',
      flightNumber: '항공편명',
      flightNumberPlaceholder: '예: KE001',
      departureAirport: '출발 공항',
      selectDepartureAirport: '출발 공항 선택',
      departureTerminal: '출발 터미널 (선택사항)',
      terminalPlaceholder: '예: 제1터미널',
      departureTime: '출발 시간',
      arrivalAirport: '도착 공항',
      selectArrivalAirport: '도착 공항 선택',
      arrivalTerminal: '도착 터미널 (선택사항)',
      arrivalTime: '도착 시간',
      flightTicket: '항공권 업로드 (선택사항)',
      uploadTicket: '항공권 업로드',
      ticketUploaded: '항공권 업로드됨',
      removeTicket: '제거',
      submit: '여행 생성',
      update: '여행 수정',
      cancel: '취소',
      requiredField: '필수 입력 항목입니다',
      invalidDate: '귀국일은 출발일 이후여야 합니다',
      invalidTime: '도착 시간은 출발 시간 이후여야 합니다'
    }
  };

  const t = translations?.[currentLanguage];

  const countryOptions = [
    { value: 'us', label: currentLanguage === 'en' ? 'United States' : '미국' },
    { value: 'jp', label: currentLanguage === 'en' ? 'Japan' : '일본' },
    { value: 'kr', label: currentLanguage === 'en' ? 'South Korea' : '대한민국' },
    { value: 'cn', label: currentLanguage === 'en' ? 'China' : '중국' },
    { value: 'uk', label: currentLanguage === 'en' ? 'United Kingdom' : '영국' },
    { value: 'fr', label: currentLanguage === 'en' ? 'France' : '프랑스' },
    { value: 'de', label: currentLanguage === 'en' ? 'Germany' : '독일' },
    { value: 'au', label: currentLanguage === 'en' ? 'Australia' : '호주' }
  ];

  const airlineOptions = [
    { value: 'KE', label: currentLanguage === 'en' ? 'Korean Air (KE)' : '대한항공 (KE)' },
    { value: 'OZ', label: currentLanguage === 'en' ? 'Asiana Airlines (OZ)' : '아시아나항공 (OZ)' },
    { value: 'AA', label: currentLanguage === 'en' ? 'American Airlines (AA)' : '아메리칸항공 (AA)' },
    { value: 'UA', label: currentLanguage === 'en' ? 'United Airlines (UA)' : '유나이티드항공 (UA)' },
    { value: 'DL', label: currentLanguage === 'en' ? 'Delta Air Lines (DL)' : '델타항공 (DL)' },
    { value: 'JL', label: currentLanguage === 'en' ? 'Japan Airlines (JL)' : '일본항공 (JL)' },
    { value: 'NH', label: currentLanguage === 'en' ? 'ANA (NH)' : '전일본공수 (NH)' },
    { value: 'BA', label: currentLanguage === 'en' ? 'British Airways (BA)' : '영국항공 (BA)' },
    { value: 'AF', label: currentLanguage === 'en' ? 'Air France (AF)' : '에어프랑스 (AF)' },
    { value: 'LH', label: currentLanguage === 'en' ? 'Lufthansa (LH)' : '루프트한자 (LH)' }
  ];

  const airportOptions = [
    { value: 'ICN', label: currentLanguage === 'en' ? 'Incheon International (ICN)' : '인천국제공항 (ICN)' },
    { value: 'GMP', label: currentLanguage === 'en' ? 'Gimpo International (GMP)' : '김포국제공항 (GMP)' },
    { value: 'PUS', label: currentLanguage === 'en' ? 'Gimhae International (PUS)' : '김해국제공항 (PUS)' },
    { value: 'JFK', label: currentLanguage === 'en' ? 'John F. Kennedy (JFK)' : '존 F. 케네디 (JFK)' },
    { value: 'LAX', label: currentLanguage === 'en' ? 'Los Angeles (LAX)' : '로스앤젤레스 (LAX)' },
    { value: 'NRT', label: currentLanguage === 'en' ? 'Narita International (NRT)' : '나리타국제공항 (NRT)' },
    { value: 'HND', label: currentLanguage === 'en' ? 'Haneda Airport (HND)' : '하네다공항 (HND)' },
    { value: 'LHR', label: currentLanguage === 'en' ? 'London Heathrow (LHR)' : '런던 히드로 (LHR)' },
    { value: 'CDG', label: currentLanguage === 'en' ? 'Charles de Gaulle (CDG)' : '샤를드골 (CDG)' },
    { value: 'FRA', label: currentLanguage === 'en' ? 'Frankfurt Airport (FRA)' : '프랑크푸르트 (FRA)' }
  ];

  const categoryOptions = [
    { value: 'electronics', label: currentLanguage === 'en' ? 'Electronics' : '전자제품' },
    { value: 'fashion', label: currentLanguage === 'en' ? 'Fashion & Accessories' : '패션 & 액세서리' },
    { value: 'cosmetics', label: currentLanguage === 'en' ? 'Cosmetics & Beauty' : '화장품 & 뷰티' },
    { value: 'food', label: currentLanguage === 'en' ? 'Food & Beverages' : '식품 & 음료' },
    { value: 'books', label: currentLanguage === 'en' ? 'Books & Media' : '도서 & 미디어' },
    { value: 'sports', label: currentLanguage === 'en' ? 'Sports & Outdoor' : '스포츠 & 아웃도어' },
    { value: 'toys', label: currentLanguage === 'en' ? 'Toys & Games' : '장난감 & 게임' },
    { value: 'health', label: currentLanguage === 'en' ? 'Health & Wellness' : '건강 & 웰니스' }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleFileChange = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      setFormData(prev => ({ ...prev, flightTicket: file }));
      setTicketPreview(URL.createObjectURL(file));
    }
  };

  const handleRemoveTicket = () => {
    setFormData(prev => ({ ...prev, flightTicket: null }));
    setTicketPreview(null);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.fromCountry) newErrors.fromCountry = t?.requiredField;
    if (!formData?.toCountry) newErrors.toCountry = t?.requiredField;
    if (!formData?.departureDate) newErrors.departureDate = t?.requiredField;
    if (!formData?.returnDate) newErrors.returnDate = t?.requiredField;
    if (!formData?.capacityKg) newErrors.capacityKg = t?.requiredField;
    if (formData?.allowedCategories?.length === 0) newErrors.allowedCategories = t?.requiredField;

    // Flight information validation (required fields)
    if (!formData?.airline) newErrors.airline = t?.requiredField;
    if (!formData?.flightNumber) newErrors.flightNumber = t?.requiredField;
    if (!formData?.departureAirport) newErrors.departureAirport = t?.requiredField;
    if (!formData?.departureTime) newErrors.departureTime = t?.requiredField;
    if (!formData?.arrivalAirport) newErrors.arrivalAirport = t?.requiredField;
    if (!formData?.arrivalTime) newErrors.arrivalTime = t?.requiredField;

    if (formData?.departureDate && formData?.returnDate) {
      if (new Date(formData?.returnDate) <= new Date(formData?.departureDate)) {
        newErrors.returnDate = t?.invalidDate;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      await onSubmit({
        fromCountry: formData?.fromCountry,
        toCountry: formData?.toCountry,
        departureDate: formData?.departureDate,
        returnDate: formData?.returnDate,
        capacity: parseInt(formData?.capacityKg),
        allowedCategories: formData?.allowedCategories,
        flightInfo: {
          airline: formData?.airline,
          flightNumber: formData?.flightNumber,
          departureAirport: formData?.departureAirport,
          departureTerminal: formData?.departureTerminal,
          departureTime: formData?.departureTime,
          arrivalAirport: formData?.arrivalAirport,
          arrivalTerminal: formData?.arrivalTerminal,
          arrivalTime: formData?.arrivalTime,
          flightTicket: formData?.flightTicket
        }
      });
      
      trackEvent('trip_creation', {
        from_country: formData?.fromCountry,
        to_country: formData?.toCountry,
        capacity: parseInt(formData?.capacityKg),
        allowed_categories_count: formData?.allowedCategories?.length,
        has_flight_info: true,
        has_ticket: !!formData?.flightTicket,
        page: '/traveler-trip-creation'
      });
      
      if (!editingTrip) {
        setFormData({
          fromCountry: '',
          toCountry: '',
          departureDate: '',
          returnDate: '',
          capacityKg: '',
          allowedCategories: [],
          airline: '',
          flightNumber: '',
          departureAirport: '',
          departureTerminal: '',
          departureTime: '',
          arrivalAirport: '',
          arrivalTerminal: '',
          arrivalTime: '',
          flightTicket: null
        });
        setTicketPreview(null);
        setErrors({});
      }
    } catch (error) {
      console.error('Trip creation error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-card rounded-lg shadow-elevation-md p-4 md:p-6 lg:p-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
          <Icon name={editingTrip ? "Edit" : "PlusCircle"} size={20} color="var(--color-primary)" />
        </div>
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
          {editingTrip ? t?.editTrip : t?.createTrip}
        </h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          {/* From Country */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              {t?.fromCountry} <span className="text-destructive">*</span>
            </label>
            <Select
              options={countryOptions}
              value={formData?.fromCountry}
              onChange={(value) => handleChange('fromCountry', value)}
              placeholder={t?.selectFrom}
              searchable
              error={errors?.fromCountry}
            />
          </div>

          {/* To Country */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              {t?.toCountry} <span className="text-destructive">*</span>
            </label>
            <Select
              options={countryOptions}
              value={formData?.toCountry}
              onChange={(value) => handleChange('toCountry', value)}
              placeholder={t?.selectTo}
              searchable
              error={errors?.toCountry}
            />
          </div>

          {/* Departure Date */}
          <div>
            <Input
              type="date"
              label={t?.departureDate}
              value={formData?.departureDate}
              onChange={(e) => handleChange('departureDate', e?.target?.value)}
              error={errors?.departureDate}
              required
            />
          </div>

          {/* Return Date */}
          <div>
            <Input
              type="date"
              label={t?.returnDate}
              value={formData?.returnDate}
              onChange={(e) => handleChange('returnDate', e?.target?.value)}
              error={errors?.returnDate}
              required
            />
          </div>

          {/* Capacity */}
          <div>
            <Input
              type="number"
              label={t?.capacity}
              value={formData?.capacityKg}
              onChange={(e) => handleChange('capacityKg', e?.target?.value)}
              placeholder={t?.capacityPlaceholder}
              error={errors?.capacityKg}
              required
              min="1"
            />
          </div>

          {/* Allowed Categories */}
          <div className="lg:col-span-2">
            <label className="text-sm font-medium text-foreground mb-2 block">
              {t?.allowedCategories} <span className="text-destructive">*</span>
            </label>
            <Select
              options={categoryOptions}
              value={formData?.allowedCategories}
              onChange={(value) => handleChange('allowedCategories', value)}
              placeholder={t?.selectCategories}
              multiple
              error={errors?.allowedCategories}
            />
          </div>
        </div>

        {/* Flight Information Section */}
        <div className="border-t border-border pt-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Plane" size={16} color="var(--color-primary)" />
            </div>
            <h3 className="text-lg font-heading font-semibold text-foreground">
              {t?.flightInfo}
            </h3>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            {/* Airline */}
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                {t?.airline} <span className="text-destructive">*</span>
              </label>
              <Select
                options={airlineOptions}
                value={formData?.airline}
                onChange={(value) => handleChange('airline', value)}
                placeholder={t?.selectAirline}
                searchable
                error={errors?.airline}
              />
            </div>

            {/* Flight Number */}
            <div>
              <Input
                type="text"
                label={t?.flightNumber}
                value={formData?.flightNumber}
                onChange={(e) => handleChange('flightNumber', e?.target?.value)}
                placeholder={t?.flightNumberPlaceholder}
                error={errors?.flightNumber}
                required
              />
            </div>

            {/* Departure Airport */}
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                {t?.departureAirport} <span className="text-destructive">*</span>
              </label>
              <Select
                options={airportOptions}
                value={formData?.departureAirport}
                onChange={(value) => handleChange('departureAirport', value)}
                placeholder={t?.selectDepartureAirport}
                searchable
                error={errors?.departureAirport}
              />
            </div>

            {/* Departure Terminal */}
            <div>
              <Input
                type="text"
                label={t?.departureTerminal}
                value={formData?.departureTerminal}
                onChange={(e) => handleChange('departureTerminal', e?.target?.value)}
                placeholder={t?.terminalPlaceholder}
                error={errors?.departureTerminal}
              />
            </div>

            {/* Departure Time */}
            <div>
              <Input
                type="time"
                label={t?.departureTime}
                value={formData?.departureTime}
                onChange={(e) => handleChange('departureTime', e?.target?.value)}
                error={errors?.departureTime}
                required
              />
            </div>

            {/* Arrival Airport */}
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                {t?.arrivalAirport} <span className="text-destructive">*</span>
              </label>
              <Select
                options={airportOptions}
                value={formData?.arrivalAirport}
                onChange={(value) => handleChange('arrivalAirport', value)}
                placeholder={t?.selectArrivalAirport}
                searchable
                error={errors?.arrivalAirport}
              />
            </div>

            {/* Arrival Terminal */}
            <div>
              <Input
                type="text"
                label={t?.arrivalTerminal}
                value={formData?.arrivalTerminal}
                onChange={(e) => handleChange('arrivalTerminal', e?.target?.value)}
                placeholder={t?.terminalPlaceholder}
                error={errors?.arrivalTerminal}
              />
            </div>

            {/* Arrival Time */}
            <div>
              <Input
                type="time"
                label={t?.arrivalTime}
                value={formData?.arrivalTime}
                onChange={(e) => handleChange('arrivalTime', e?.target?.value)}
                error={errors?.arrivalTime}
                required
              />
            </div>

            {/* Flight Ticket Upload */}
            <div className="lg:col-span-2">
              <label className="text-sm font-medium text-foreground mb-2 block">
                {t?.flightTicket}
              </label>
              {!ticketPreview ? (
                <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary transition-base cursor-pointer">
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    onChange={handleFileChange}
                    className="hidden"
                    id="ticket-upload"
                  />
                  <label htmlFor="ticket-upload" className="cursor-pointer">
                    <Icon name="Upload" size={32} color="var(--color-muted-foreground)" className="mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground mb-1">{t?.uploadTicket}</p>
                    <p className="text-xs text-muted-foreground">PDF, PNG, JPG (Max 10MB)</p>
                  </label>
                </div>
              ) : (
                <div className="border border-border rounded-lg p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
                      <Icon name="FileCheck" size={20} color="var(--color-success)" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">{t?.ticketUploaded}</p>
                      <p className="text-xs text-muted-foreground">
                        {formData?.flightTicket?.name || 'Existing ticket'}
                      </p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    iconName="X"
                    onClick={handleRemoveTicket}
                    className="text-destructive hover:text-destructive"
                  >
                    {t?.removeTicket}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex gap-3 pt-4">
          {editingTrip && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancelEdit}
              className="flex-1"
            >
              {t?.cancel}
            </Button>
          )}
          <Button
            type="submit"
            loading={loading}
            iconName={editingTrip ? "Check" : "PlusCircle"}
            className="flex-1"
          >
            {editingTrip ? t?.update : t?.submit}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default TripForm;